import type { OptionItem } from "@certa/types";
import type {
  AppSettingPermissionList,
  AppSettings,
  AppSettingsRaw,
  AppSettingsJSONSchema,
  AppSettingsJSONSchemaRaw,
  JSONSchemaRaw,
  JSONSchema,
  ObjectStatusRaw
} from "../types/appSettings.types";
import { JSONSchemaFieldType } from "../types/appSettings.types";
import { appSettingsRawToAppSettingsMap } from "../constants/appSettings.constants";

export const alertCategoryModelCreator = (
  alertcategories: AppSettingsRaw["alertcategory"]
): AppSettings["alertCategory"] =>
  alertcategories.map(alertcategory => ({
    name: alertcategory.name,
    isVisible: alertcategory.is_visible,
    canSubscribe: alertcategory.can_subscribe,
    helpText: alertcategory.help_text,
    visibilityPriority: alertcategory.visibility_priority,
    tag: alertcategory.tag,
    colorLabel: alertcategory.color_label,
    parentCategory: alertcategory.parent_category
  }));

export const businessUnitModelCreator = (
  businessunits: AppSettingsRaw["businessunit"]
): AppSettings["businessUnit"] =>
  businessunits.map(bu => ({
    code: bu.code,
    name: bu.name,
    // After deploying, order of this array is not consistent for the live settings API call
    // so sorting it to ensure a consistent order
    regions: bu.regions?.sort()
  }));

export const commentFlagOptionModelCreator = (
  commentflagoptions: AppSettingsRaw["commentflagoption"]
): AppSettings["commentFlagOption"] =>
  commentflagoptions.map(commentflagoption => ({
    name: commentflagoption.name,
    reasonCode: commentflagoption.reason_code,
    color: commentflagoption.extra?.color,
    borderColor: commentflagoption.extra?.borderColor,
    backgroundColor: commentflagoption.extra?.backgroundColor,
    tag: commentflagoption.tag,
    postToActivityLog: commentflagoption.post_to_activity_log,
    // After deploying, order of this array is not consistent for the live settings API call
    // so sorting it to ensure a consistent order
    userGroups: commentflagoption.user_groups?.sort()
  }));

export const emailTemplateModelCreator = (
  emailtemplates: AppSettingsRaw["emailtemplate"]
): AppSettings["emailTemplate"] =>
  emailtemplates.map(emailtemplate => ({
    slug: emailtemplate.slug,
    title: emailtemplate.title,
    subject: emailtemplate.subject,
    replyTo: emailtemplate.reply_to,
    fromEmail: emailtemplate.from_email,
    fromName: emailtemplate.from_name,
    text: emailtemplate.text,
    html: emailtemplate.html,
    isAsync: emailtemplate.is_async,
    // After deploying, order of this array is not consistent for the live settings API call
    // so sorting it to ensure a consistent order
    attachments: emailtemplate.attachments?.sort(),
    attributes: emailtemplate.attributes
  }));

export const groupModelCreator = (
  groups: AppSettingsRaw["group"]
): AppSettings["group"] =>
  groups.map(group => ({
    name: group.name,
    // After deploying, order of this array is not consistent for the live settings API call
    // so sorting it to ensure a consistent order
    permissions: group.permissions?.sort()
  }));

export const processKindModelCreator = (
  kinds: AppSettingsRaw["workflowkind"]
): AppSettings["processKind"] =>
  kinds.map(kind => ({
    name: kind.name,
    tag: kind.tag,
    isActive: kind.is_active,
    commentsAllowed: kind.comments_allowed,
    defaultStatus: kind.default_status,
    // After deploying, order of this array is not consistent for the live settings API call
    // so sorting it to ensure a consistent order
    availableStatuses: kind.available_statuses?.sort()
  }));

export const pdfTemplatesModelCreator = (
  pdfTemplates: AppSettingsRaw["workflowpdftemplate"]
): AppSettings["processPDFTemplate"] =>
  pdfTemplates.map(pdfTemplate => ({
    name: pdfTemplate.name,
    html: pdfTemplate.html,
    isStatic: pdfTemplate.is_static,
    tag: pdfTemplate.tag
  }));

export const processStatusModelCreator = (
  processStatuses: AppSettingsRaw["workflowstatus"]
): AppSettings["processStatus"] =>
  processStatuses.map(processStatus => ({
    kind: processStatus.kind,
    label: processStatus.label,
    tag: processStatus.tag,
    colorCode: processStatus.color_code || undefined
  }));

const objectStatusModelCreator = (
  objectStatuses: AppSettingsRaw["objectstatus"]
): AppSettings["objectStatus"] =>
  objectStatuses?.map(objectStatus => ({
    name: objectStatus.name,
    tag: objectStatus.tag,
    colorCode: objectStatus.color_code
  })) ?? [];

export const objectTypeWorkflowKindRelationModelCreator = (
  objectTypeWFKindRelation: AppSettingsRaw["objecttypeworkflowkindrelation"][0]
): AppSettings["objectTypeWorkflowKindRelation"][0] => ({
  objectType: objectTypeWFKindRelation.object_type,
  workflowKind: objectTypeWFKindRelation.workflow_kind,
  relation: objectTypeWFKindRelation.relation,
  extra: objectTypeWFKindRelation.extra
});

const integrationConfigModelCreator = (
  integrationRawConfig: AppSettingsRaw["integrationconfig"][0]
): AppSettings["integrationConfig"][0] => ({
  name: integrationRawConfig.name,
  url: integrationRawConfig.url,
  method: integrationRawConfig.method,
  username: integrationRawConfig.username,
  password: integrationRawConfig.password,
  requestBodyType: integrationRawConfig.request_body_type,
  requestBody: integrationRawConfig.request_body,
  advancedBodyRendering: integrationRawConfig.advanced_body_rendering,
  requestHeader: integrationRawConfig.request_header,
  requestThreshold: integrationRawConfig.request_threshold,
  responseListJsonPath: integrationRawConfig.response_list_json_path,
  responseListJmespath: integrationRawConfig.response_list_jmespath,
  responseListUuidKey: integrationRawConfig.response_list_uuid_key,
  fileType: integrationRawConfig.file_type,
  fileJsonPath: integrationRawConfig.file_json_path,
  fileNameJsonPath: integrationRawConfig.file_name_json_path,
  childWorkflowRequestBody: integrationRawConfig.child_workflow_request_body,
  dataObjectRequestBody: integrationRawConfig.data_object_request_body,
  clientCertificatePrivate: integrationRawConfig.client_certificate_private,
  pollingInterval: integrationRawConfig.polling_interval,
  failureEmails: integrationRawConfig.failure_emails,
  statusJsonPath: integrationRawConfig.status_json_path,
  apiType: integrationRawConfig.api_type,
  childWorkflowKind: integrationRawConfig.child_workflow_kind,
  dataObjectKind: integrationRawConfig.data_object_kind,
  integrationToken: integrationRawConfig.integration_token,
  nameMatchingParams: integrationRawConfig.name_matching_params,
  extra: integrationRawConfig.extra
});

const integrationTokenModelCreator = (
  integrationTokenRaw: AppSettingsRaw["integrationtoken"][0]
): AppSettings["integrationToken"][0] => ({
  isStatic: integrationTokenRaw.is_static,
  username: integrationTokenRaw.username,
  password: integrationTokenRaw.password,
  tokenUrl: integrationTokenRaw.token_url,
  tokenHeaders: integrationTokenRaw.token_headers,
  tokenBody: integrationTokenRaw.token_body,
  tokenJsonPath: integrationTokenRaw.token_json_path,
  freshTokenRequired: integrationTokenRaw.fresh_token_required,
  token: integrationTokenRaw.token,
  refreshInterval: integrationTokenRaw.refresh_interval,
  clientCertificatePrivate: integrationTokenRaw.client_certificate_private,
  tokenType: integrationTokenRaw.token_type
});

export const appSettingsModelCreator = (
  response: AppSettingsRaw
): AppSettings => {
  return {
    [appSettingsRawToAppSettingsMap.alertcategory]: alertCategoryModelCreator(
      response.alertcategory
    ),
    [appSettingsRawToAppSettingsMap.businessunit]: response.businessunit.map(
      bu => ({
        code: bu.code,
        name: bu.name,
        // After deploying, order of this array is not consistent for the live settings API call
        // so sorting it to ensure a consistent order
        regions: bu.regions?.sort()
      })
    ),
    [appSettingsRawToAppSettingsMap.commentflagoption]:
      commentFlagOptionModelCreator(response.commentflagoption),
    // constanceConfig: response.constance_config || {},
    [appSettingsRawToAppSettingsMap.emailattachment]:
      response.emailattachment.map(emailattachment => ({
        attachment: emailattachment.attachment,
        title: emailattachment.title
      })),
    [appSettingsRawToAppSettingsMap.emailtemplate]: emailTemplateModelCreator(
      response.emailtemplate
    ),
    [appSettingsRawToAppSettingsMap.extrajson]: response.extrajson.map(
      extrajson => ({
        data: extrajson.data,
        description: extrajson.description,
        tag: extrajson.tag
      })
    ),
    [appSettingsRawToAppSettingsMap.group]: response.group.map(group => {
      const nonReadonlyPermissions = [...(group.permissions || [])];
      return {
        name: group.name,
        // After deploying, order of this array is not consistent for the live settings API call
        // so sorting it to ensure a consistent order
        permissions: nonReadonlyPermissions.sort()
      };
    }),
    [appSettingsRawToAppSettingsMap.region]: response.region.map(region => ({
      code: region.code,
      name: region.name
    })),
    [appSettingsRawToAppSettingsMap.workflowkind]: processKindModelCreator(
      response.workflowkind
    ),
    [appSettingsRawToAppSettingsMap.workflowpdftemplate]:
      pdfTemplatesModelCreator(response.workflowpdftemplate),
    [appSettingsRawToAppSettingsMap.workflowstatus]: processStatusModelCreator(
      response.workflowstatus
    ),
    [appSettingsRawToAppSettingsMap.objectstatus]: objectStatusModelCreator(
      response.objectstatus || []
    ),
    [appSettingsRawToAppSettingsMap.objecttypeworkflowkindrelation]: (
      response.objecttypeworkflowkindrelation || []
    ).map(objectTypeWorkflowKindRelationModelCreator),
    [appSettingsRawToAppSettingsMap.integrationconfig]: (
      response.integrationconfig || []
    ).map(integrationConfigModelCreator),
    [appSettingsRawToAppSettingsMap.integrationtoken]: (
      response.integrationtoken || []
    ).map(integrationTokenModelCreator),
    [appSettingsRawToAppSettingsMap.frontend_extra_metadata]:
      response.frontend_extra_metadata
  };
};

export const alertCategoryPayloadCreator = (
  alertCategory: AppSettings["alertCategory"][0]
): AppSettingsRaw["alertcategory"][0] => ({
  can_subscribe: alertCategory.canSubscribe,
  help_text: alertCategory.helpText,
  is_visible: alertCategory.isVisible,
  name: alertCategory.name,
  tag: alertCategory.tag,
  visibility_priority: alertCategory.visibilityPriority,
  color_label: alertCategory.colorLabel,
  parent_category: alertCategory.parentCategory
});

export const businessUnitPayloadCreator = (
  businessUnit: AppSettings["businessUnit"][0]
): AppSettingsRaw["businessunit"][0] => businessUnit;

export const commentFlagOptionPayloadCreator = (
  commentFlagOption: AppSettings["commentFlagOption"][0]
): AppSettingsRaw["commentflagoption"][0] => ({
  post_to_activity_log: commentFlagOption.postToActivityLog,
  tag: commentFlagOption.tag,
  user_groups: commentFlagOption.userGroups,
  extra: {
    backgroundColor: commentFlagOption.backgroundColor,
    borderColor: commentFlagOption.borderColor,
    color: commentFlagOption.color
  },
  name: commentFlagOption.name,
  reason_code: commentFlagOption.reasonCode
});

export const emailAttachmentPayloadCreator = (
  emailAttachment: AppSettings["emailAttachment"][0]
): AppSettingsRaw["emailattachment"][0] => emailAttachment;

export const emailTemplatePayloadCreator = (
  emailTemplate: AppSettings["emailTemplate"][0]
): AppSettingsRaw["emailtemplate"][0] => ({
  attachments: emailTemplate.attachments,
  from_email: emailTemplate.fromEmail,
  from_name: emailTemplate.fromName,
  html: emailTemplate.html,
  is_async: emailTemplate.isAsync,
  reply_to: emailTemplate.replyTo,
  slug: emailTemplate.slug,
  subject: emailTemplate.subject,
  text: emailTemplate.text,
  title: emailTemplate.title,
  attributes: emailTemplate.attributes
});

export const extraJSONPayloadCreator = (
  extraJSON: AppSettings["extraJSON"][0]
): AppSettingsRaw["extrajson"][0] => ({
  data: extraJSON.data,
  description: extraJSON.description,
  tag: extraJSON.tag
});

export const groupPayloadCreator = (
  group: AppSettings["group"][0]
): AppSettingsRaw["group"][0] => group;

export const regionPayloadCreator = (
  region: AppSettings["region"][0]
): AppSettingsRaw["region"][0] => region;

export const processKindPayloadCreator = (
  processKind: AppSettings["processKind"][0]
): AppSettingsRaw["workflowkind"][0] => ({
  available_statuses: processKind.availableStatuses,
  comments_allowed: processKind.commentsAllowed,
  default_status: processKind.defaultStatus,
  is_active: processKind.isActive,
  name: processKind.name,
  tag: processKind.tag
});

export const processPDFTemplatePayloadCreator = (
  processPDFTemplate: AppSettings["processPDFTemplate"][0]
): AppSettingsRaw["workflowpdftemplate"][0] => ({
  html: processPDFTemplate.html,
  is_static: processPDFTemplate.isStatic,
  name: processPDFTemplate.name,
  tag: processPDFTemplate.tag
});

export const processStatusPayloadCreator = (
  processStatus: AppSettings["processStatus"][0]
): AppSettingsRaw["workflowstatus"][0] => ({
  kind: processStatus.kind,
  label: processStatus.label,
  tag: processStatus.tag,
  color_code: processStatus.colorCode
});

export const objectStatusPayloadCreator = (
  objectStatus: AppSettings["objectStatus"][0]
): ObjectStatusRaw => ({
  name: objectStatus.name,
  tag: objectStatus.tag,
  color_code: objectStatus.colorCode
});

export const objectTypeWorkflowKindRelationPayloadCreator = (
  objectTypeWorkflowKindRelation: AppSettings["objectTypeWorkflowKindRelation"][0]
): AppSettingsRaw["objecttypeworkflowkindrelation"][0] => ({
  object_type: objectTypeWorkflowKindRelation.objectType,
  workflow_kind: objectTypeWorkflowKindRelation.workflowKind,
  relation: objectTypeWorkflowKindRelation.relation,
  extra: objectTypeWorkflowKindRelation.extra
});

export const integrationConfigPayloadCreator = (
  integrationConfig: AppSettings["integrationConfig"][0]
): AppSettingsRaw["integrationconfig"][0] => {
  const parseExtra = (extra: string) => {
    try {
      return JSON.parse(extra);
    } catch (error) {
      return undefined;
    }
  };

  return {
    name: integrationConfig.name,
    url: integrationConfig.url,
    method: integrationConfig.method,
    username: integrationConfig.username,
    password: integrationConfig.password,
    request_body_type: integrationConfig.requestBodyType,
    request_body: integrationConfig.requestBody,
    advanced_body_rendering: integrationConfig.advancedBodyRendering,
    request_header: integrationConfig.requestHeader,
    request_threshold: integrationConfig.requestThreshold,
    response_list_json_path: integrationConfig.responseListJsonPath,
    response_list_jmespath: integrationConfig.responseListJmespath,
    response_list_uuid_key: integrationConfig.responseListUuidKey,
    file_type: integrationConfig.fileType,
    file_json_path: integrationConfig.fileJsonPath,
    file_name_json_path: integrationConfig.fileNameJsonPath,
    child_workflow_request_body: integrationConfig.childWorkflowRequestBody,
    data_object_request_body: integrationConfig.dataObjectRequestBody,
    client_certificate_private: integrationConfig.clientCertificatePrivate,
    polling_interval: integrationConfig.pollingInterval,
    failure_emails: integrationConfig.failureEmails,
    status_json_path: integrationConfig.statusJsonPath,
    api_type: integrationConfig.apiType,
    child_workflow_kind: integrationConfig.childWorkflowKind,
    data_object_kind: integrationConfig.dataObjectKind,
    integration_token: integrationConfig.integrationToken,
    name_matching_params: integrationConfig.nameMatchingParams,
    extra:
      typeof integrationConfig.extra === "string"
        ? parseExtra(integrationConfig.extra)
        : integrationConfig.extra
  };
};

export const integrationTokenPayloadCreator = (
  integrationToken: AppSettings["integrationToken"][0]
): AppSettingsRaw["integrationtoken"][0] => ({
  is_static: integrationToken.isStatic,
  username: integrationToken.username,
  password: integrationToken.password,
  token_url: integrationToken.tokenUrl,
  token_headers: integrationToken.tokenHeaders,
  token_body: integrationToken.tokenBody,
  token_json_path: integrationToken.tokenJsonPath,
  fresh_token_required: integrationToken.freshTokenRequired,
  token: integrationToken.token,
  refresh_interval: integrationToken.refreshInterval,
  client_certificate_private: integrationToken.clientCertificatePrivate,
  token_type: integrationToken.tokenType
});

export const appSettingsPayloadCreator = (
  appSettings: AppSettings
): AppSettingsRaw => {
  let filePaths: string[] = [];

  const alertcategory = appSettings.alertCategory.map(
    alertCategoryPayloadCreator
  );
  const businessunit = appSettings.businessUnit.map(businessUnitPayloadCreator);
  const commentflagoption = appSettings.commentFlagOption.map(
    commentFlagOptionPayloadCreator
  );
  const emailattachment = appSettings.emailAttachment.map(
    emailAttachmentPayloadCreator
  );

  /**
   * We may need a regex check here to only put paths from the new
   * upload API and not all the attachment URLs (that are imported and
   * may have older style of paths)
   */
  emailattachment.forEach(attachment => {
    filePaths = [...filePaths, attachment.attachment];
  });

  const emailtemplate = appSettings.emailTemplate.map(
    emailTemplatePayloadCreator
  );
  const extrajson = appSettings.extraJSON.map(extraJSONPayloadCreator);
  const group = appSettings.group.map(groupPayloadCreator);
  const region = appSettings.region.map(regionPayloadCreator);
  const workflowkind = appSettings.processKind.map(processKindPayloadCreator);
  const workflowpdftemplate = appSettings.processPDFTemplate.map(
    processPDFTemplatePayloadCreator
  );
  const workflowstatus = appSettings.processStatus.map(
    processStatusPayloadCreator
  );
  const objectstatus = appSettings.objectStatus.map(objectStatusPayloadCreator);
  const objecttypeworkflowkindrelation =
    appSettings.objectTypeWorkflowKindRelation.map(
      objectTypeWorkflowKindRelationPayloadCreator
    );
  const integrationconfig = appSettings.integrationConfig.map(
    integrationConfigPayloadCreator
  );
  const integrationtoken = appSettings.integrationToken.map(
    integrationTokenPayloadCreator
  );
  const frontendExtraMetadata = appSettings.feExtraMetaData;

  return {
    alertcategory,
    businessunit,
    commentflagoption,
    emailattachment,
    emailtemplate,
    extrajson,
    group,
    region,
    workflowkind,
    workflowpdftemplate,
    workflowstatus,
    objectstatus,
    objecttypeworkflowkindrelation,
    integrationconfig,
    integrationtoken,
    file_paths: filePaths,
    frontend_extra_metadata: frontendExtraMetadata
  };
};

export const appSettingsPermissionListModelCreator = (
  data: AppSettingPermissionList
): OptionItem[] => {
  const permissions = [];
  for (const scope in data) {
    for (const model in data[scope]) {
      for (const permission in data[scope][model]) {
        permissions.push({
          label: data[scope][model][permission],
          value: `${permission}|${scope}|${model}`
        });
      }
    }
  }
  return permissions;
};

type Def = AppSettingsJSONSchemaRaw["definitions"];
export const appSettingsSchemaModelCreator = (
  data: AppSettingsJSONSchemaRaw
): AppSettingsJSONSchema => {
  const {
    definitions: {
      emailtemplate,
      emailattachment,
      businessunit,
      region,
      extrajson,
      workflowkind,
      workflowstatus,
      objectstatus,
      workflowpdftemplate,
      group,
      commentflagoption,
      alertcategory,
      objecttypeworkflowkindrelation,
      integrationconfig,
      integrationtoken
    }
  } = data;

  return {
    emailTemplate: {
      slug: generateSchema<Def["emailtemplate"]>(emailtemplate, "slug"),
      title: generateSchema<Def["emailtemplate"]>(emailtemplate, "title"),
      subject: generateSchema<Def["emailtemplate"]>(emailtemplate, "subject"),
      replyTo: generateSchema<Def["emailtemplate"]>(emailtemplate, "reply_to"),
      fromEmail: generateSchema<Def["emailtemplate"]>(
        emailtemplate,
        "from_email"
      ),
      fromName: generateSchema<Def["emailtemplate"]>(
        emailtemplate,
        "from_name"
      ),
      text: generateSchema<Def["emailtemplate"]>(emailtemplate, "text"),
      html: generateSchema<Def["emailtemplate"]>(emailtemplate, "html"),
      isAsync: generateSchema<Def["emailtemplate"]>(emailtemplate, "is_async"),
      attachments: generateSchema<Def["emailtemplate"]>(
        emailtemplate,
        "attachments"
      ),
      attributes: generateSchema<Def["emailtemplate"]>(
        emailtemplate,
        "attributes"
      )
    },
    emailAttachment: {
      title: generateSchema<Def["emailattachment"]>(emailattachment, "title"),
      attachment: generateSchema<Def["emailattachment"]>(
        emailattachment,
        "attachment"
      )
    },
    businessUnit: {
      code: generateSchema<Def["businessunit"]>(businessunit, "code"),
      name: generateSchema<Def["businessunit"]>(businessunit, "name"),
      regions: generateSchema<Def["businessunit"]>(businessunit, "regions")
    },
    region: {
      code: generateSchema<Def["region"]>(region, "code"),
      name: generateSchema<Def["region"]>(region, "name")
    },
    extraJSON: {
      tag: generateSchema<Def["extrajson"]>(extrajson, "tag"),
      description: generateSchema<Def["extrajson"]>(extrajson, "description"),
      data: generateSchema<Def["extrajson"]>(extrajson, "data")
    },
    processKind: {
      name: generateSchema<Def["workflowkind"]>(workflowkind, "name"),
      tag: generateSchema<Def["workflowkind"]>(workflowkind, "tag"),
      isActive: generateSchema<Def["workflowkind"]>(workflowkind, "is_active"),
      commentsAllowed: generateSchema<Def["workflowkind"]>(
        workflowkind,
        "comments_allowed"
      ),
      defaultStatus: generateSchema<Def["workflowkind"]>(
        workflowkind,
        "default_status"
      ),
      availableStatuses: generateSchema<Def["workflowkind"]>(
        workflowkind,
        "available_statuses"
      )
    },
    processStatus: {
      label: generateSchema<Def["workflowstatus"]>(workflowstatus, "label"),
      kind: generateSchema<Def["workflowstatus"]>(workflowstatus, "kind"),
      tag: generateSchema<Def["workflowstatus"]>(workflowstatus, "tag"),
      colorCode: generateSchema<Def["workflowstatus"]>(
        workflowstatus,
        "color_code"
      )
    },
    objectStatus: {
      name: generateSchema<Def["objectstatus"]>(objectstatus, "name"),
      tag: generateSchema<Def["objectstatus"]>(objectstatus, "tag"),
      colorCode: generateSchema<Def["objectstatus"]>(objectstatus, "color_code")
    },
    processPDFTemplate: {
      name: generateSchema<Def["workflowpdftemplate"]>(
        workflowpdftemplate,
        "name"
      ),
      html: generateSchema<Def["workflowpdftemplate"]>(
        workflowpdftemplate,
        "html"
      ),
      isStatic: generateSchema<Def["workflowpdftemplate"]>(
        workflowpdftemplate,
        "is_static"
      ),
      tag: generateSchema<Def["workflowpdftemplate"]>(
        workflowpdftemplate,
        "tag"
      )
    },
    alertCategory: {
      name: generateSchema<Def["alertcategory"]>(alertcategory, "name"),
      isVisible: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "is_visible"
      ),
      canSubscribe: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "can_subscribe"
      ),
      helpText: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "help_text"
      ),
      visibilityPriority: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "visibility_priority"
      ),
      tag: generateSchema<Def["alertcategory"]>(alertcategory, "tag"),
      colorLabel: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "color_label"
      ),
      parentCategory: generateSchema<Def["alertcategory"]>(
        alertcategory,
        "parent_category"
      )
    },
    commentFlagOption: {
      name: generateSchema<Def["commentflagoption"]>(commentflagoption, "name"),
      reasonCode: generateSchema<Def["commentflagoption"]>(
        commentflagoption,
        "reason_code"
      ),
      tag: generateSchema<Def["commentflagoption"]>(commentflagoption, "tag"),
      postToActivityLog: generateSchema<Def["commentflagoption"]>(
        commentflagoption,
        "post_to_activity_log"
      ),
      userGroups: generateSchema<Def["commentflagoption"]>(
        commentflagoption,
        "user_groups"
      ),
      /* Composite fields that are part of commentFlagOption.extra */
      color: { isRequired: false, type: JSONSchemaFieldType.string },
      borderColor: { isRequired: false, type: JSONSchemaFieldType.string },
      backgroundColor: { isRequired: false, type: JSONSchemaFieldType.string }
    },
    group: {
      name: generateSchema<Def["group"]>(group, "name"),
      permissions: generateSchema<Def["group"]>(group, "permissions")
    },
    objectTypeWorkflowKindRelation: {
      objectType: generateSchema<Def["objecttypeworkflowkindrelation"]>(
        objecttypeworkflowkindrelation,
        "object_type"
      ),
      workflowKind: generateSchema<Def["objecttypeworkflowkindrelation"]>(
        objecttypeworkflowkindrelation,
        "workflow_kind"
      ),
      relation: generateSchema<Def["objecttypeworkflowkindrelation"]>(
        objecttypeworkflowkindrelation,
        "relation"
      ),
      extra: generateSchema<Def["objecttypeworkflowkindrelation"]>(
        objecttypeworkflowkindrelation,
        "extra"
      )
    },
    integrationConfig: {
      name: generateSchema<Def["integrationconfig"]>(integrationconfig, "name"),
      url: generateSchema<Def["integrationconfig"]>(integrationconfig, "url"),
      method: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "method"
      ),
      username: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "username"
      ),
      password: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "password"
      ),
      requestBodyType: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "request_body_type"
      ),
      requestBody: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "request_body"
      ),
      advancedBodyRendering: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "advanced_body_rendering"
      ),
      requestHeader: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "request_header"
      ),
      requestThreshold: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "request_threshold"
      ),
      responseListJsonPath: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "response_list_json_path"
      ),
      responseListJmespath: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "response_list_jmespath"
      ),
      responseListUuidKey: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "response_list_uuid_key"
      ),
      fileType: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "file_type"
      ),
      fileJsonPath: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "file_json_path"
      ),
      fileNameJsonPath: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "file_name_json_path"
      ),
      childWorkflowRequestBody: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "child_workflow_request_body"
      ),
      dataObjectRequestBody: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "data_object_request_body"
      ),
      clientCertificatePrivate: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "client_certificate_private"
      ),
      pollingInterval: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "polling_interval"
      ),
      failureEmails: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "failure_emails"
      ),
      statusJsonPath: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "status_json_path"
      ),
      apiType: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "api_type"
      ),
      childWorkflowKind: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "child_workflow_kind"
      ),
      dataObjectKind: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "data_object_kind"
      ),
      integrationToken: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "integration_token"
      ),
      nameMatchingParams: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "name_matching_params"
      ),
      extra: generateSchema<Def["integrationconfig"]>(
        integrationconfig,
        "extra"
      )
    },
    integrationToken: {
      isStatic: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "is_static"
      ),
      username: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "username"
      ),
      password: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "password"
      ),
      tokenUrl: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "token_url"
      ),
      tokenHeaders: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "token_headers"
      ),
      tokenBody: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "token_body"
      ),
      tokenJsonPath: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "token_json_path"
      ),
      freshTokenRequired: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "fresh_token_required"
      ),
      token: generateSchema<Def["integrationtoken"]>(integrationtoken, "token"),
      refreshInterval: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "refresh_interval"
      ),
      clientCertificatePrivate: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "client_certificate_private"
      ),
      tokenType: generateSchema<Def["integrationtoken"]>(
        integrationtoken,
        "token_type"
      )
    }
  };
};

/**
 * Generates a schema that can be utitlized to build DDF schema properties for a field
 */
const generateSchema = <
  T extends {
    properties: Record<string, JSONSchemaRaw>;
    required: string[];
    unique_identifier: string;
  }
>(
  data: T,
  // Extracting string keys from the union of all keys of type T
  // This is just to bypass TS error, keys of T.properties are always string
  key: Extract<keyof T["properties"], string>
): JSONSchema => {
  const { required, properties, unique_identifier: uniqueIdentifier } = data;
  const { maxLength, default: defaultValue, blank: isBlank } = properties[key];
  const types = properties[key].type;

  return {
    isRequired: required.includes(key),
    maxLength,
    default: defaultValue,
    isBlankValueAllowed: isBlank !== false,
    type: types.length > 1 ? types : types[0],
    unique: properties[key]?.unique || false,
    isUniqueIdentifier: uniqueIdentifier === key
  };
};
